import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Phone, MessageCircle, Globe } from 'lucide-react';
import { contactInfo } from '../mockData';

export const Header = ({ language, setLanguage, t, showLanguageToggle = true }) => {
  const [isVisible, setIsVisible] = useState(true);
  const timeoutRef = useRef(null);
  const isHoveredRef = useRef(false);

  const startHideTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!isHoveredRef.current) {
      timeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 2000);
    }
  }, []);

  const handleScroll = useCallback(() => {
    setIsVisible(true);
    startHideTimer();
  }, [startHideTimer]);

  useEffect(() => {
    startHideTimer();
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [handleScroll, startHideTimer]);

  const handleMouseEnter = () => {
    isHoveredRef.current = true;
    setIsVisible(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;
    startHideTimer();
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/91${contactInfo.phone}?text=${encodeURIComponent(contactInfo.whatsappMessage)}`;
    window.open(url, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:+91${contactInfo.phone}`;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white shadow-sm transition-transform duration-500 ease-in-out ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="RajDarshan Tandon Dairy"
              className="h-12 w-auto sm:h-14"
            />
            <div className="hidden sm:block">
              <h1 className="text-2xl font-bold text-[#1A4A8F] font-serif">RajDarshan Farms</h1>
              <p className="text-xs text-[#1A4A8F]/70 font-semibold">{t.footerTagline}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {showLanguageToggle && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
                className="gap-2 border-brand-navy/20 hover:bg-brand-lightBlue text-brand-navy font-semibold"
              >
                <Globe className="h-4 w-4" />
                <span className="hidden sm:inline">{language === 'en' ? 'हिंदी' : 'English'}</span>
              </Button>
            )}
            <Button
              onClick={handleCall}
              className="gap-2 bg-brand-pink hover:bg-brand-pink/90 text-white font-semibold"
              size="sm"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden sm:inline">{t.ctaCall}</span>
            </Button>
            <Button
              onClick={handleWhatsApp}
              className="gap-2 bg-whatsapp hover:bg-[#1DA851] text-white font-semibold flex items-center"
              size="sm"
            >
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
